import React from 'react';
import { Check, ChevronRight, Loader2, Mail, RotateCcw, Send } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { emailMotDePasseOubliAdminApi, problemeTexteEmail } from '../../lib/motDePasseOubli';
import { Field, Input, Select, Textarea } from '../ui/Field';
import { pillGhost, pillPrimary, pillSecondary } from '../ui/pills';

/**
 * Le texte de l'e-mail « Mot de passe oublié », réglable par le super-admin.
 *
 * Posé en tête de la fenêtre « Emails des comptes clients » (EmailsDuModule) : c'est là que vivent déjà
 * les autres e-mails de mot de passe. Même carte, mêmes champs, même bouton d'insertion de variables que
 * les modèles voisins — mais un enregistrement à part, parce que le bouton et le lien sont posés par le
 * système et que cet e-mail n'est pas un texte libre en un bloc.
 *
 * L'aperçu est le rendu EXACT du serveur (même gabarit que l'envoi), affiché dans un cadre isolé.
 */
type Valeurs = { subject: string; intro: string; button_label: string; outro: string; sender_name: string; validity_minutes: number };
type ChampTexte = 'subject' | 'intro' | 'button_label' | 'outro' | 'sender_name';

const DUREES = [
    { v: 15, l: '15 minutes' }, { v: 30, l: '30 minutes' }, { v: 60, l: '1 heure (recommandé)' },
    { v: 120, l: '2 heures' }, { v: 360, l: '6 heures' }, { v: 1440, l: '24 heures' },
];

// Variables insérables : pas dans le libellé du bouton ni dans le nom d'expéditeur, où elles n'ont pas cours.
const VARIABLES = [
    { token: '{prenom}', label: 'Prénom' },
    { token: '{nom}', label: 'Nom' },
    { token: '{entreprise}', label: 'Établissement' },
    { token: '{duree}', label: 'Durée de validité' },
    { token: '{lien}', label: 'Lien (adresse)' },
];

export const EmailMotDePasseOublie: React.FC = () => {
    const [valeurs, setValeurs] = React.useState<Valeurs | null>(null);
    const [defauts, setDefauts] = React.useState<Valeurs | null>(null);
    const [limites, setLimites] = React.useState<Record<string, number>>({});
    const [erreurs, setErreurs] = React.useState<Record<string, string>>({});
    const [chargement, setChargement] = React.useState(true);
    const [ouvert, setOuvert] = React.useState(false);
    const [action, setAction] = React.useState<'' | 'enregistrement' | 'test'>('');
    const [retour, setRetour] = React.useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);
    const [apercu, setApercu] = React.useState<{ subject: string; html: string; fromName: string } | null>(null);
    const [champActif, setChampActif] = React.useState<'subject' | 'intro' | 'outro'>('intro');
    const refs = {
        subject: React.useRef<HTMLInputElement>(null),
        intro: React.useRef<HTMLTextAreaElement>(null),
        outro: React.useRef<HTMLTextAreaElement>(null),
    };

    React.useEffect(() => {
        emailMotDePasseOubliAdminApi.lire()
            .then((r: any) => { setValeurs(r.valeurs); setDefauts(r.defauts); setLimites(r.limites || {}); })
            .catch(() => setRetour({ type: 'erreur', texte: 'Impossible de charger les réglages.' }))
            .finally(() => setChargement(false));
    }, []);

    // Aperçu en direct : le serveur rend l'e-mail du brouillon, après une courte pause de frappe.
    React.useEffect(() => {
        if (!valeurs) return;
        const minuteur = setTimeout(() => {
            emailMotDePasseOubliAdminApi.apercu(valeurs).then((r: any) => setApercu(r)).catch(() => setApercu(null));
        }, 350);
        return () => clearTimeout(minuteur);
    }, [valeurs]);

    const maj = (champ: keyof Valeurs, valeur: string | number) => {
        setValeurs((v) => (v ? { ...v, [champ]: valeur } : v));
        // Faute détectée dès la frappe (mêmes règles que le serveur) ; sinon l'erreur du champ disparaît.
        const faute = typeof valeur === 'string' ? problemeTexteEmail(champ, valeur, limites[champ]) : null;
        setErreurs((e) => { const { [champ]: _, ...reste } = e; return faute ? { ...reste, [champ]: faute } : reste; });
        setRetour(null);
    };

    const inserer = (token: string) => {
        if (!valeurs) return;
        const el = refs[champActif].current as HTMLInputElement | HTMLTextAreaElement | null;
        const actuel = valeurs[champActif];
        const debut = el?.selectionStart ?? actuel.length;
        const fin = el?.selectionEnd ?? actuel.length;
        maj(champActif, actuel.slice(0, debut) + token + actuel.slice(fin));
        requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(debut + token.length, debut + token.length); });
    };

    const enregistrer = async () => {
        if (!valeurs) return;
        if (Object.keys(erreurs).length) { setRetour({ type: 'erreur', texte: "Corrigez les champs signalés avant d'enregistrer." }); return; }
        setAction('enregistrement'); setRetour(null);
        try {
            const r: any = await emailMotDePasseOubliAdminApi.enregistrer(valeurs);
            setValeurs(r.valeurs); setErreurs({});
            setRetour({ type: 'ok', texte: 'Enregistré. Les prochains e-mails utiliseront ce texte.' });
        } catch (e: any) {
            setRetour({ type: 'erreur', texte: e?.message || "L'enregistrement a échoué." });
        } finally { setAction(''); }
    };

    const tester = async () => {
        if (!valeurs) return;
        setAction('test'); setRetour(null);
        try {
            const r: any = await emailMotDePasseOubliAdminApi.tester(valeurs);
            setRetour({ type: 'ok', texte: `E-mail de test envoyé à ${r.destinataire}. Le lien qu'il contient ne fonctionne pas.` });
        } catch (e: any) {
            setRetour({ type: 'erreur', texte: e?.message || "L'e-mail de test n'a pas pu être envoyé." });
        } finally { setAction(''); }
    };

    const retablir = () => {
        if (!defauts) return;
        setValeurs({ ...defauts }); setErreurs({});
        setRetour({ type: 'ok', texte: "Texte par défaut chargé. Cliquez sur « Enregistrer » pour l'appliquer." });
    };

    if (chargement) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (!valeurs) return <p role="alert" className="text-sm text-state-danger">{retour?.texte}</p>;

    const compteur = (champ: ChampTexte) => `${valeurs[champ].length} / ${limites[champ] ?? ''}`;
    const prise = (champ: 'subject' | 'intro' | 'outro') => ({ onFocus: () => setChampActif(champ) });

    return (
        <>
            <section aria-labelledby="titre-mdp-oublie" className="rounded-2xl border border-border overflow-hidden">
                <button type="button" onClick={() => setOuvert(true)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-surface hover:bg-surface-soft transition-colors text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                    <span className="min-w-0 flex-1">
                        <span id="titre-mdp-oublie" className="block text-[13.5px] font-semibold leading-snug text-content">Mot de passe oublié</span>
                        <span className="block text-[12px] text-content-muted leading-snug mt-0.5">Part quand quelqu'un clique sur « Mot de passe oublié ? » à la connexion. Le bouton est toujours ajouté par le système.</span>
                    </span>
                    <ChevronRight size={16} className="text-content-muted shrink-0" aria-hidden="true" />
                    <span className="text-[10.5px] font-semibold text-content-muted shrink-0 rounded-full border border-border px-2 py-0.5 whitespace-nowrap">Sur clic</span>
                </button>
            </section>

            <Modal open={ouvert} onClose={() => setOuvert(false)} widthPx={1100} title="Mot de passe oublié" icon={<Mail size={16} />}
                subtitle="Texte simple : pas de HTML, les retours à la ligne sont conservés. Un champ vide reprend le texte par défaut." footerLayout="split"
                footer={(
                    <>
                        <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={tester} disabled={action !== ''} className={pillSecondary(36)}>
                                <Send size={13} aria-hidden="true" /> {action === 'test' ? 'Envoi…' : "M'envoyer un e-mail de test"}
                            </button>
                            <button type="button" onClick={retablir} disabled={action !== ''} className={pillGhost(36)}>
                                <RotateCcw size={13} aria-hidden="true" /> Rétablir le texte par défaut
                            </button>
                        </div>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setOuvert(false)} className={pillSecondary(36)}>Fermer</button>
                            <button type="button" onClick={enregistrer} disabled={action !== ''} className={pillPrimary(36)}>
                                {action === 'enregistrement' ? 'Enregistrement…' : 'Enregistrer'}
                            </button>
                        </div>
                    </>
                )}>
                <div className="grid gap-5 lg:grid-cols-2">
                    <div className="space-y-4 min-w-0">
                    <div className="grid gap-4">
                        <Field label="Objet" error={erreurs.subject} counter={compteur('subject')}>
                            {({ id, describedBy, invalid }) => (
                                <Input id={id} ref={refs.subject} value={valeurs.subject} maxLength={limites.subject} invalid={invalid} aria-describedby={describedBy}
                                    onChange={(e) => maj('subject', e.target.value)} {...prise('subject')} />
                            )}
                        </Field>
                        <Field label="Texte d'introduction" error={erreurs.intro} counter={compteur('intro')}>
                            {({ id, describedBy, invalid }) => (
                                <Textarea id={id} ref={refs.intro} rows={6} value={valeurs.intro} maxLength={limites.intro} invalid={invalid} aria-describedby={describedBy}
                                    onChange={(e) => maj('intro', e.target.value)} {...prise('intro')} />
                            )}
                        </Field>
                        <Field label="Libellé du bouton" error={erreurs.button_label} counter={compteur('button_label')}>
                            {({ id, describedBy, invalid }) => (
                                <Input id={id} value={valeurs.button_label} maxLength={limites.button_label} invalid={invalid} aria-describedby={describedBy}
                                    onChange={(e) => maj('button_label', e.target.value)} />
                            )}
                        </Field>
                        <Field label="Texte de fin" error={erreurs.outro} counter={compteur('outro')}>
                            {({ id, describedBy, invalid }) => (
                                <Textarea id={id} ref={refs.outro} rows={3} value={valeurs.outro} maxLength={limites.outro} invalid={invalid} aria-describedby={describedBy}
                                    onChange={(e) => maj('outro', e.target.value)} {...prise('outro')} />
                            )}
                        </Field>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Nom d'expéditeur affiché" error={erreurs.sender_name} counter={compteur('sender_name')}>
                                {({ id, describedBy, invalid }) => (
                                    <Input id={id} value={valeurs.sender_name} maxLength={limites.sender_name} invalid={invalid} aria-describedby={describedBy}
                                        onChange={(e) => maj('sender_name', e.target.value)} />
                                )}
                            </Field>
                            <Field label="Durée de validité du lien" error={erreurs.validity_minutes} help="C'est aussi la durée réelle : le lien cesse de fonctionner après ce délai.">
                                {({ id, describedBy, invalid }) => (
                                    <Select id={id} value={valeurs.validity_minutes} invalid={invalid} aria-describedby={describedBy}
                                        onChange={(e) => maj('validity_minutes', Number(e.target.value))}>
                                        {DUREES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                                    </Select>
                                )}
                            </Field>
                        </div>
                    </div>

                    <div>
                        <p className="text-[10px] text-content-muted mb-1.5">Cliquez pour insérer une variable dans l'objet, l'introduction ou le texte de fin (celui où se trouve votre curseur) :</p>
                        <div className="flex flex-wrap gap-1.5">
                            {VARIABLES.map((t) => (
                                <button key={t.token} type="button" onClick={() => inserer(t.token)}
                                    className="px-2.5 py-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-brand-500 hover:border-brand-500 text-[11px] font-semibold transition-colors">
                                    {t.label} <span className="font-mono opacity-70">{t.token}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    </div>
                    <div className="min-w-0 lg:sticky lg:top-0 lg:self-start">
                    <div>
                        <h3 className="text-xs font-black uppercase tracking-wider text-content-muted">Aperçu</h3>
                        {apercu ? (
                            <div className="mt-2 rounded-xl border border-border overflow-hidden">
                                <dl className="px-3 py-2 text-xs bg-surface-soft border-b border-border grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                                    <dt className="text-content-muted">De</dt><dd className="text-content truncate">{apercu.fromName}</dd>
                                    <dt className="text-content-muted">Objet</dt><dd className="text-content truncate">{apercu.subject}</dd>
                                </dl>
                                {/* Cadre isolé (sandbox vide) : le rendu serveur s'affiche, aucun script ne peut s'y exécuter. */}
                                <iframe title="Aperçu de l'e-mail" sandbox="" srcDoc={apercu.html} className="w-full h-[420px] bg-white" />
                            </div>
                        ) : (
                            <div className="mt-2 flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
                        )}
                        <p className="mt-1.5 text-[11px] text-content-muted">Exemple avec un compte fictif (Camille, Brasserie du Vieux Port) et un faux lien.</p>
                    </div>
                    </div>
                </div>
                    <p role="status" aria-live="polite" className={`min-h-[1.25rem] text-[12.5px] inline-flex items-start gap-1.5 ${retour?.type === 'erreur' ? 'text-state-danger' : 'text-state-ok'}`}>
                        {retour && (<>{retour.type === 'ok' && <Check size={14} className="mt-0.5 shrink-0" aria-hidden="true" />}<span>{retour.texte}</span></>)}
                    </p>
            </Modal>
        </>
    );
};
